/* crm-automation.jsx — Phase 5: Automation Engine, Workflow Execution, Approvals */
const { useState, useEffect, useMemo } = React;

const STATUS_COLORS = { SUCCESS: '#16A34A', FAILED: '#EF4444', PENDING: '#F59E0B', RUNNING: '#3B82F6' };
const TRIGGER_ICONS = { DEAL_CREATED: 'Plus', DEAL_STAGE_CHANGED: 'ArrowRight', DEAL_WON: 'CheckCircle', DEAL_LOST: 'XCircle', TASK_OVERDUE: 'AlertCircle', TASK_COMPLETED: 'Check', EMAIL_RECEIVED: 'Mail', MEETING_COMPLETED: 'Video', SCHEDULED: 'Clock', MANUAL: 'Play' };

/* ── Condition Builder ── */
const ConditionBuilder = ({ conditions, onChange }) => {
  const rows = conditions?.rules || [];
  const op = conditions?.operator || 'AND';

  const addRow = () => onChange({ operator: op, rules: [...rows, { field: 'stage', operator: 'equals', value: '' }] });
  const removeRow = (i) => onChange({ operator: op, rules: rows.filter((_, idx) => idx !== i) });
  const updateRow = (i, key, val) => onChange({ operator: op, rules: rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r) });

  return (
    <div style={{ border: '1px solid var(--nx-border)', borderRadius: 8, padding: 12, background: 'var(--nx-bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', fontWeight: 600 }}>MATCH</span>
        {['AND', 'OR'].map(o => (
          <button key={o} onClick={() => onChange({ ...conditions, operator: o })} style={{ padding: '3px 10px', borderRadius: 6, border: `1px solid ${op === o ? 'var(--nx-accent)' : 'var(--nx-border)'}`, background: op === o ? 'var(--nx-accent-tint-2)' : 'transparent', color: op === o ? 'var(--nx-accent)' : 'var(--nx-text-muted)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{o}</button>
        ))}
        <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>of the conditions</span>
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr 28px', gap: 6, marginBottom: 6 }}>
          <input value={r.field} onChange={e => updateRow(i, 'field', e.target.value)} placeholder="field" style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }} />
          <select value={r.operator} onChange={e => updateRow(i, 'operator', e.target.value)} style={{ padding: '5px 6px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }}>
            {['equals', 'not_equals', 'contains', 'greater_than', 'less_than', 'is_empty', 'is_not_empty'].map(o => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
          </select>
          <input value={r.value} onChange={e => updateRow(i, 'value', e.target.value)} placeholder="value" style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }} />
          <button onClick={() => removeRow(i)} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer' }}><NexusIcon name="X" size={13} /></button>
        </div>
      ))}
      <button onClick={addRow} style={{ padding: '5px 12px', borderRadius: 6, border: '1px dashed var(--nx-border)', background: 'transparent', color: 'var(--nx-text-muted)', fontSize: 12, cursor: 'pointer', marginTop: 4 }}>+ Add Condition</button>
    </div>
  );
};

/* ── Action Config Panel ── */
const ActionConfigPanel = ({ action, onChange, onRemove }) => {
  const ACTION_TYPES = ['SEND_EMAIL', 'CREATE_TASK', 'CREATE_MEETING', 'SEND_NOTIFICATION', 'UPDATE_FIELD', 'ASSIGN_OWNER', 'ADD_TAG', 'WEBHOOK', 'DELAY'];
  return (
    <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--nx-accent-tint-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
        <NexusIcon name="Zap" size={13} style={{ color: 'var(--nx-accent)' }} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <select value={action.type || 'SEND_NOTIFICATION'} onChange={e => onChange({ ...action, type: e.target.value })} style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13, fontWeight: 600 }}>
          {ACTION_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        {(action.type === 'SEND_NOTIFICATION' || !action.type) && (
          <input value={action.message || ''} onChange={e => onChange({ ...action, message: e.target.value })} placeholder="Notification message..." style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }} />
        )}
        {action.type === 'SEND_EMAIL' && (
          <input value={action.subject || ''} onChange={e => onChange({ ...action, subject: e.target.value })} placeholder="Email subject..." style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }} />
        )}
        {action.type === 'CREATE_TASK' && (
          <input value={action.title || ''} onChange={e => onChange({ ...action, title: e.target.value })} placeholder="Task title..." style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }} />
        )}
        {action.type === 'DELAY' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="number" value={action.delayMinutes || 60} onChange={e => onChange({ ...action, delayMinutes: +e.target.value })} style={{ width: 80, padding: '6px 8px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12 }} />
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>minutes</span>
          </div>
        )}
      </div>
      <button onClick={onRemove} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', padding: 4 }}><NexusIcon name="Trash2" size={13} /></button>
    </div>
  );
};

/* ── Automation Rule Card ── */
const AutomationRuleCard = ({ rule, onToggle, onDelete, onViewLogs }) => (
  <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '14px 16px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 34, height: 34, borderRadius: 8, background: rule.isActive ? 'var(--nx-accent-tint-2)' : 'var(--nx-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid var(--nx-border)' }}>
        <NexusIcon name={TRIGGER_ICONS[rule.triggerEvent] || 'Zap'} size={16} style={{ color: rule.isActive ? 'var(--nx-accent)' : 'var(--nx-text-muted)' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{rule.name}</span>
          <span style={{ padding: '1px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: rule.isActive ? '#16A34A18' : 'var(--nx-border)', color: rule.isActive ? '#16A34A' : 'var(--nx-text-muted)' }}>{rule.isActive ? 'ACTIVE' : 'PAUSED'}</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
          {rule.triggerEvent?.replace(/_/g, ' ')} · {(rule.actions || []).length} action{(rule.actions || []).length !== 1 ? 's' : ''} · {rule.runCount || 0} runs
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onViewLogs(rule.id)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 11, color: 'var(--nx-text-muted)', cursor: 'pointer' }}>Logs</button>
        <button onClick={() => onToggle(rule.id, !rule.isActive)} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: rule.isActive ? '#F59E0B18' : '#16A34A18', fontSize: 11, fontWeight: 600, color: rule.isActive ? '#F59E0B' : '#16A34A', cursor: 'pointer' }}>
          {rule.isActive ? 'Pause' : 'Activate'}
        </button>
        <button onClick={() => onDelete(rule.id)} style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: 'transparent', color: '#EF4444', cursor: 'pointer' }}><NexusIcon name="Trash2" size={13} /></button>
      </div>
    </div>
  </div>
);

/* ── Automation Rule Editor ── */
const AutomationRuleEditor = ({ onSave, onCancel }) => {
  const [form, setForm] = useState({ name: '', triggerEvent: 'DEAL_CREATED', conditions: { operator: 'AND', rules: [] }, actions: [{ type: 'SEND_NOTIFICATION', message: '' }], isActive: false });
  const TRIGGERS = ['DEAL_CREATED', 'DEAL_STAGE_CHANGED', 'DEAL_WON', 'DEAL_LOST', 'TASK_OVERDUE', 'TASK_COMPLETED', 'EMAIL_RECEIVED', 'MEETING_COMPLETED', 'MEETING_SCHEDULED', 'SCHEDULED', 'MANUAL'];

  const addAction = () => setForm(f => ({ ...f, actions: [...f.actions, { type: 'SEND_NOTIFICATION', message: '' }] }));
  const updateAction = (i, a) => setForm(f => ({ ...f, actions: f.actions.map((x, idx) => idx === i ? a : x) }));
  const removeAction = (i) => setForm(f => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));

  return (
    <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-accent)', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text)' }}>New Automation Rule</div>
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)' }}>RULE NAME</label>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Notify on Deal Won" style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13, boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)' }}>TRIGGER EVENT</label>
        <select value={form.triggerEvent} onChange={e => setForm(f => ({ ...f, triggerEvent: e.target.value }))} style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 13 }}>
          {TRIGGERS.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
      </div>
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 6, display: 'block' }}>CONDITIONS (OPTIONAL)</label>
        <ConditionBuilder conditions={form.conditions} onChange={c => setForm(f => ({ ...f, conditions: c }))} />
      </div>
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 8, display: 'block' }}>ACTIONS</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {form.actions.map((a, i) => <ActionConfigPanel key={i} action={a} onChange={upd => updateAction(i, upd)} onRemove={() => removeAction(i)} />)}
          <button onClick={addAction} style={{ padding: '7px 12px', borderRadius: 7, border: '1px dashed var(--nx-border)', background: 'transparent', color: 'var(--nx-accent)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add Action</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '7px 16px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 13, color: 'var(--nx-text-muted)', cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onSave(form)} style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Save Rule</button>
      </div>
    </div>
  );
};

/* ── Automation Logs Panel ── */
const AutomationLogsPanel = ({ ruleId }) => {
  const [logs, setLogs] = useState([]);
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  useEffect(() => {
    const load = async () => {
      if (!wsId || !ruleId || !window.__nx_api?.automation?.getLogs) return;
      const d = await window.__nx_api.automation.getLogs(wsId, ruleId).catch(() => []);
      setLogs(Array.isArray(d) ? d : []);
    };
    load();
  }, [wsId, ruleId]);

  if (!logs.length) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>No logs yet.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ borderBottom: '1px solid var(--nx-border)' }}>{['Time', 'Status', 'Actions Run', 'Duration', 'Error'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--nx-text-muted)', fontWeight: 600 }}>{h}</th>)}</tr></thead>
        <tbody>
          {logs.map((l, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--nx-border)' }}>
              <td style={{ padding: '8px 10px', color: 'var(--nx-text)' }}>{new Date(l.createdAt).toLocaleString()}</td>
              <td style={{ padding: '8px 10px' }}><span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: (STATUS_COLORS[l.status] || '#64748B') + '18', color: STATUS_COLORS[l.status] || '#64748B' }}>{l.status}</span></td>
              <td style={{ padding: '8px 10px', color: 'var(--nx-text)' }}>{l.actionsRun}</td>
              <td style={{ padding: '8px 10px', color: 'var(--nx-text-muted)' }}>{l.durationMs ? `${l.durationMs}ms` : '—'}</td>
              <td style={{ padding: '8px 10px', color: '#EF4444', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.error || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── Automation Manager View ── */
const AutomationManagerView = () => {
  const [rules, setRules] = useState([]);
  const [tab, setTab] = useState('rules');
  const [creating, setCreating] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [loading, setLoading] = useState(false);
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast?.() || {};

  useEffect(() => {
    const load = async () => {
      if (!wsId || !window.__nx_api?.automation?.list) return;
      const d = await window.__nx_api.automation.list(wsId).catch(() => []);
      setRules(Array.isArray(d) ? d : []);
    };
    load();
  }, [wsId]);

  const handleSave = async (form) => {
    if (!form.name || !wsId) return;
    setLoading(true);
    try {
      const r = await window.__nx_api?.automation?.create(wsId, form);
      if (r) { setRules(prev => [r, ...prev]); setCreating(false); toast.show?.({ title: 'Automation rule created', type: 'success' }); }
    } catch { toast.show?.({ title: 'Failed to create rule', type: 'error' }); }
    setLoading(false);
  };

  const handleToggle = async (id, isActive) => {
    if (!wsId) return;
    try {
      const r = await window.__nx_api?.automation?.toggle(wsId, id, isActive);
      if (r) setRules(prev => prev.map(x => x.id === id ? r : x));
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!wsId) return;
    try { await window.__nx_api?.automation?.remove(wsId, id); setRules(prev => prev.filter(x => x.id !== id)); } catch {}
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--nx-text)', display: 'flex', alignItems: 'center', gap: 8 }}><NexusIcon name="Zap" size={18} style={{ color: 'var(--nx-accent)' }} /> Automation Engine</div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>Rule-based automation for email, meetings, tasks, notifications</div>
        </div>
        <button onClick={() => setCreating(true)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <NexusIcon name="Plus" size={13} /> New Rule
        </button>
      </div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--nx-border)', padding: '0 20px' }}>
        {['rules', 'logs'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 16px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: tab === t ? 700 : 400, color: tab === t ? 'var(--nx-accent)' : 'var(--nx-text-muted)', borderBottom: tab === t ? '2px solid var(--nx-accent)' : '2px solid transparent', cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {tab === 'rules' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {creating && <AutomationRuleEditor onSave={handleSave} onCancel={() => setCreating(false)} />}
            {rules.length === 0 && !creating && (
              <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--nx-text-muted)' }}>
                <NexusIcon name="Zap" size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No automation rules yet</div>
                <div style={{ fontSize: 13 }}>Create rules to automate emails, tasks, notifications and more.</div>
              </div>
            )}
            {rules.map(r => <AutomationRuleCard key={r.id} rule={r} onToggle={handleToggle} onDelete={handleDelete} onViewLogs={(id) => { setSelectedRuleId(id); setTab('logs'); }} />)}
          </div>
        )}
        {tab === 'logs' && (
          <div>
            {selectedRuleId ? (
              <>
                <button onClick={() => setSelectedRuleId(null)} style={{ marginBottom: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', color: 'var(--nx-text-muted)', fontSize: 12, cursor: 'pointer' }}>← All Rules</button>
                <AutomationLogsPanel ruleId={selectedRuleId} />
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--nx-text-muted)', fontSize: 13 }}>Select a rule from the Rules tab to view its execution logs.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Approvals View ── */
const ApprovalsView = () => {
  const [rules, setRules] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState('requests');
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast?.() || {};

  useEffect(() => {
    const load = async () => {
      if (!wsId) return;
      const [r, req] = await Promise.all([
        window.__nx_api?.approvals?.listRules?.(wsId).catch(() => []),
        window.__nx_api?.approvals?.listRequests?.(wsId).catch(() => []),
      ]);
      setRules(Array.isArray(r) ? r : []);
      setRequests(Array.isArray(req) ? req : []);
    };
    load();
  }, [wsId]);

  const handleVote = async (id, decision) => {
    try {
      await window.__nx_api?.approvals?.vote?.(wsId, id, { decision, userId: window.__nx_api?.session?.currentUser?.id });
      toast.show?.({ title: `${decision === 'APPROVE' ? 'Approved' : 'Rejected'}`, type: decision === 'APPROVE' ? 'success' : 'error' });
      const updated = await window.__nx_api?.approvals?.listRequests?.(wsId).catch(() => null);
      if (updated) setRequests(updated);
    } catch {}
  };

  const REQ_COLOR = { PENDING: '#F59E0B', APPROVED: '#16A34A', REJECTED: '#EF4444' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--nx-text)', display: 'flex', alignItems: 'center', gap: 8 }}><NexusIcon name="Shield" size={18} style={{ color: 'var(--nx-accent)' }} /> Approval Workflows</div>
      </div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--nx-border)', padding: '0 20px' }}>
        {['requests', 'rules'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 16px', border: 'none', background: 'transparent', fontSize: 13, fontWeight: tab === t ? 700 : 400, color: tab === t ? 'var(--nx-accent)' : 'var(--nx-text-muted)', borderBottom: tab === t ? '2px solid var(--nx-accent)' : '2px solid transparent', cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {tab === 'requests' && (requests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--nx-text-muted)', fontSize: 13 }}>No pending approval requests.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {requests.map(req => (
              <div key={req.id} style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)' }}>{req.entityType} approval request</div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>Requested {new Date(req.createdAt).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: (REQ_COLOR[req.status] || '#64748B') + '18', color: REQ_COLOR[req.status] || '#64748B' }}>{req.status}</span>
                    {req.status === 'PENDING' && (
                      <>
                        <button onClick={() => handleVote(req.id, 'APPROVE')} style={{ padding: '5px 12px', borderRadius: 7, border: 'none', background: '#16A34A', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Approve</button>
                        <button onClick={() => handleVote(req.id, 'REJECT')} style={{ padding: '5px 12px', borderRadius: 7, border: 'none', background: '#EF4444', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Reject</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
        {tab === 'rules' && (
          <div style={{ padding: '8px 0', color: 'var(--nx-text-muted)', fontSize: 13 }}>
            {rules.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48 }}>No approval rules configured.</div>
            ) : rules.map(r => (
              <div key={r.id} style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '14px 16px', marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{r.name}</div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{r.entityType} · {r.approvalType} · {(r.approvers || []).length} approver(s)</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

window.AutomationManagerView = AutomationManagerView;
window.ApprovalsView = ApprovalsView;
